/**
 * Numeric input cho các form New/Edit Futures Control.
 * - Chỉ nhận digits (+ 1 dấu chấm thập phân nếu `integer={false}`); chữ cái / `e` / `+` bị chặn.
 * - `allowNegative` (mặc định false): cho phép 1 dấu `-` đầu chuỗi; mặc định `-` bị chặn.
 * - Khi blur: hiển thị format "standard" (thousand separator); khi focus: raw để dễ sửa.
 * - Right-aligned giống cột số trong datasheet.
 */
import { TextField, type TextFieldProps } from '@mui/material'
import { useState, type FocusEvent } from 'react'

type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: string
  onChange: (raw: string) => void
  /** true: chỉ digits; false (mặc định): digits + tối đa 1 dấu chấm. */
  integer?: boolean
  /** true: cho phép số âm (1 dấu `-` đầu chuỗi); mặc định false. */
  allowNegative?: boolean
}

const sanitize = (s: string, integer: boolean, allowNegative: boolean): string => {
  const neg = allowNegative && /^\s*-/.test(s) ? '-' : ''
  const cleaned = s.replace(integer ? /[^0-9]/g : /[^0-9.]/g, '')
  if (integer) return neg + cleaned
  const dot = cleaned.indexOf('.')
  if (dot === -1) return neg + cleaned
  return neg + cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '')
}

/**
 * Format kiểu Access "Standard": thousand separator, giữ nguyên phần thập phân user đã gõ
 * (kể cả trailing dot/zero) để khi focus lại không bị "snap" mất ký tự. Giữ dấu `-` riêng
 * để số âm như `-0.5` không bị mất dấu khi `Number('-0')` → `-0`.
 */
const formatStandard = (raw: string, integer: boolean): string => {
  if (raw === '' || raw === '.' || raw === '-' || raw === '-.') return raw
  const sign = raw.startsWith('-') ? '-' : ''
  const abs = sign ? raw.slice(1) : raw
  if (integer) {
    const n = Number(abs)
    return Number.isFinite(n) ? sign + n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : raw
  }
  const dot = abs.indexOf('.')
  if (dot === -1) {
    const n = Number(abs)
    return Number.isFinite(n) ? sign + n.toLocaleString('en-US') : raw
  }
  const intPart = abs.slice(0, dot)
  const fracPart = abs.slice(dot + 1)
  const intNum = Number(intPart || '0')
  if (!Number.isFinite(intNum)) return raw
  return `${sign}${intNum.toLocaleString('en-US')}.${fracPart}`
}

export default function NumericInput({
  value,
  onChange,
  integer = false,
  allowNegative = false,
  inputProps,
  onFocus,
  onBlur,
  ...rest
}: Props) {
  const [focused, setFocused] = useState(false)
  const display = focused ? value : formatStandard(value, integer)

  return (
    <TextField
      {...rest}
      value={display}
      onChange={(e) => {
        // Khi blur (display có dấu phẩy) — strip dấu phẩy trước khi sanitize.
        const incoming = focused ? e.target.value : e.target.value.replace(/,/g, '')
        onChange(sanitize(incoming, integer, allowNegative))
      }}
      onFocus={(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(true)
        onFocus?.(e)
      }}
      onBlur={(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false)
        onBlur?.(e)
      }}
      inputProps={{
        inputMode: integer ? 'numeric' : 'decimal',
        ...inputProps,
        style: { textAlign: 'right', ...(inputProps?.style as React.CSSProperties | undefined) },
      }}
    />
  )
}
